import { useState } from 'react';
import { Button, Card, Modal } from '@playerbase/ui';
import {
  NPS_OPTIONS,
  RATING_VALUES,
  SEVERITY_LEVELS,
  resolveBinaryLabels,
  resolveRatingDisplay,
  resolveSelectConfig,
} from '@playerbase/types';

import type {
  BuilderField,
  BuilderMetadata,
} from '../../../games/use-form-builder-state.js';

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  metadata: BuilderMetadata;
  fields: BuilderField[];
}

type Tab = 'modal' | 'posted';

// Approximate preview of the Discord modal a player would see plus the
// posted button-message. Colours and layout mirror Discord's actual
// dark-mode chrome (see .docs/feedback-forms/v2-references/) so studios
// catch wording, ordering and option-set issues before enabling.
//
// We hard-code Discord-flavoured Tailwind classes here on purpose: the
// preview's job is to look like Discord, not like the rest of the
// portal. Numbers come from the reference screenshots.
export function PreviewModal({ open, onClose, metadata, fields }: PreviewModalProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('modal');

  return (
    <Modal open={open} onClose={onClose} labelledById="form-preview-title" size="lg">
      <Card>
        <Card.Header>
          <Card.Title id="form-preview-title">Preview</Card.Title>
          <Card.Description>
            How the form will look in Discord. Submit is inert here.
          </Card.Description>
        </Card.Header>
        <Card.Content className="flex flex-col gap-4">
          <div role="tablist" aria-label="Preview tabs" className="flex gap-2 border-b border-border">
            <PreviewTab value="modal" current={tab} onSelect={setTab}>
              Modal
            </PreviewTab>
            <PreviewTab value="posted" current={tab} onSelect={setTab}>
              Posted message
            </PreviewTab>
          </div>

          {tab === 'modal' ? (
            <ModalPreview metadata={metadata} fields={fields} />
          ) : (
            <PostedMessagePreview metadata={metadata} />
          )}
        </Card.Content>
        <Card.Footer>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </Card.Footer>
      </Card>
    </Modal>
  );
}

function PreviewTab({
  value,
  current,
  onSelect,
  children,
}: {
  value: Tab;
  current: Tab;
  onSelect: (next: Tab) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = value === current;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onSelect(value)}
      className={
        active
          ? 'border-b-2 border-accent px-3 py-1.5 text-sm text-text'
          : 'border-b-2 border-transparent px-3 py-1.5 text-sm text-text-muted hover:text-text'
      }
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal preview - mirrors the Discord modal a player sees on Submit.
// ---------------------------------------------------------------------------

function ModalPreview({
  metadata,
  fields,
}: {
  metadata: BuilderMetadata;
  fields: BuilderField[];
}): JSX.Element {
  // Discord modal shows the Discord title; the portal name is only for
  // the studio.
  const title = metadata.title === '' ? 'Untitled form' : metadata.title;
  return (
    <div className="overflow-hidden rounded-lg bg-discord-surface text-discord-text shadow-xl">
      {/* Header strip: PB avatar + form title + close icon. */}
      <div className="flex items-center gap-3 border-b border-discord-border px-5 py-4">
        <div
          aria-hidden
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-discord-surface-input text-[10px] font-bold text-discord-text"
        >
          PB
        </div>
        <div className="flex-1 truncate text-lg font-semibold text-white">{title}</div>
        <span aria-hidden className="text-base text-discord-text-muted">
          ×
        </span>
      </div>

      {/* Scrollable body. The real modal scrolls past ~5 fields; we cap
          our preview at a sensible max-height so the studio can see the
          form is dense without giving it the entire viewport. */}
      <div className="max-h-[28rem] space-y-5 overflow-y-auto px-5 py-4">
        {/* Discord-mandated warning banner. Wording is the studio name
            in real life; we use a generic placeholder. */}
        <div className="flex items-start gap-2 rounded-md border border-discord-warning bg-discord-warning-bg px-3 py-2.5 text-xs text-discord-warning">
          <span aria-hidden>⚠️</span>
          <span>
            This form will be submitted to <strong>PlayerBase</strong>. Do not
            share passwords or other sensitive information.
          </span>
        </div>

        {fields.length === 0 ? (
          <p className="text-xs text-discord-text-faint">No fields yet.</p>
        ) : null}
        {fields.map((field) => (
          <FieldPreview key={field.clientId} field={field} />
        ))}
      </div>

      {/* Footer with Cancel / Submit. Both inert in the preview. */}
      <div className="flex items-center justify-end gap-2 bg-discord-surface px-5 py-3">
        <span className="cursor-not-allowed rounded-[3px] px-4 py-1.5 text-sm text-white hover:bg-white/5">
          Cancel
        </span>
        <span className="cursor-not-allowed rounded-[3px] bg-discord-blurple px-4 py-1.5 text-sm font-medium text-white">
          Submit
        </span>
      </div>
    </div>
  );
}

function FieldPreview({ field }: { field: BuilderField }): JSX.Element {
  const title =
    field.title === '' ? <em className="text-discord-text-faint">Untitled question</em> : field.title;
  const requiredMark = field.required ? <span className="text-discord-danger"> *</span> : null;
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-semibold text-white">
        {title}
        {requiredMark}
      </div>
      {field.description !== null && field.description !== '' ? (
        <div className="text-xs text-discord-text-muted">{field.description}</div>
      ) : null}
      <div className="pt-1">
        <FieldControlPreview field={field} />
      </div>
    </div>
  );
}

function FieldControlPreview({ field }: { field: BuilderField }): JSX.Element {
  // Per-kind config resolution lives in @playerbase/types so this
  // preview can never drift from the bot's actual modal renderer.
  switch (field.kind) {
    case 'short_text':
      return <DiscordTextInput rows={1} />;
    case 'long_text':
      return <DiscordTextInput rows={4} />;
    case 'rating': {
      const display = resolveRatingDisplay(field.config);
      const emoji = display?.mode === 'emoji' ? display.emoji : null;
      return (
        <DiscordRadioStack
          items={RATING_VALUES.map((n) => ({
            key: String(n),
            label: emoji !== null ? emoji.repeat(n) : String(n),
          }))}
        />
      );
    }
    case 'select': {
      const { maxValues } = resolveSelectConfig(field.config);
      const multi = maxValues > 1;
      return (
        <div className="space-y-2">
          {multi ? (
            <div className="text-[11px] text-discord-text-faint">Pick up to {maxValues}</div>
          ) : null}
          <DiscordSelect />
        </div>
      );
    }
    case 'severity':
      return (
        <DiscordRadioStack
          items={SEVERITY_LEVELS.map((label) => ({ key: label, label }))}
        />
      );
    case 'binary': {
      const { trueLabel, falseLabel } = resolveBinaryLabels(field.config);
      return (
        <DiscordRadioStack
          items={[
            { key: 'true', label: trueLabel },
            { key: 'false', label: falseLabel },
          ]}
        />
      );
    }
    case 'nps':
      return (
        <DiscordRadioStack
          items={NPS_OPTIONS.map((option) => ({
            key: String(option.value),
            label: option.label,
          }))}
        />
      );
  }
}

// ---------------------------------------------------------------------------
// Discord-flavoured primitives. Visual only - none of these accept input.
// ---------------------------------------------------------------------------

function DiscordTextInput({ rows }: { rows: number }): JSX.Element {
  // Single-line: explicit height. Multi-line: derived from rows for the
  // Paragraph style. The real Discord input is borderless on idle; the
  // subtle ring appears on focus, which we don't simulate.
  const minHeight = rows === 1 ? '2.25rem' : `${rows * 1.4 + 0.75}rem`;
  return (
    <div
      className="w-full rounded-[3px] bg-discord-surface-input px-2 py-1.5"
      style={{ minHeight }}
      aria-hidden
    />
  );
}

function DiscordSelect(): JSX.Element {
  return (
    <div
      aria-hidden
      className="flex w-full items-center justify-between rounded-[3px] border border-transparent bg-discord-surface-input px-3 py-2 text-sm text-discord-text-faint"
    >
      <span>Make a selection</span>
      <span aria-hidden className="text-xs">
        ▾
      </span>
    </div>
  );
}

function DiscordRadioStack({
  items,
}: {
  items: Array<{ key: string; label: string }>;
}): JSX.Element {
  return (
    <div className="space-y-1.5">
      {items.map((item) => (
        <div key={item.key} className="flex items-center gap-3">
          <span
            aria-hidden
            className="h-4 w-4 shrink-0 rounded-full border border-discord-text-faint"
          />
          <span className="text-sm text-discord-text">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Posted-message preview - the channel post that carries the open-form
// button. Bold title, description below in muted, then the button.
// ---------------------------------------------------------------------------

function PostedMessagePreview({ metadata }: { metadata: BuilderMetadata }): JSX.Element {
  const title = metadata.title === '' ? 'Untitled form' : metadata.title;
  return (
    <div className="rounded-lg bg-discord-surface px-5 py-4 text-discord-text">
      <div className="text-base font-semibold text-white">{title}</div>
      {metadata.discordDescription !== '' ? (
        <div className="mt-1 text-sm text-discord-text-muted">{metadata.discordDescription}</div>
      ) : null}
      <div className="mt-3">
        <span className="inline-flex cursor-not-allowed items-center gap-2 rounded-[3px] bg-discord-blurple px-3 py-1.5 text-sm font-medium text-white">
          <span aria-hidden>📝</span>
          Share Feedback
        </span>
      </div>
    </div>
  );
}
